import React, {useEffect, useState} from "react";
import ReactECharts from "echarts-for-react";
import { Box, Grid, useMediaQuery, CircularProgress } from "@mui/material";
import {useTheme} from '@mui/material/styles';
import dayjs from "dayjs";
import { useParams } from "react-router-dom";
import ImpactGoogleMap, { impactGoogleMapContainerStyle } from "./ImpactGoogleMap";
import { useGetImpactLineCharts } from "services/ContentOpenManager";
import { REFETCH_OPTIONS } from "services/ContentManager";
import { formatCompactDurationUpToYears } from "services/helper";
import { useTranslation } from "react-i18next";

export const metricConfig = {
  totalKm: { isPercentage: false, titleTranslationKey: 'totalKmMetricTitle', descriptionTranslationKey : 'totalKmMetricDescription', symbol: 'km' },
  co2: { isPercentage: false, titleTranslationKey: 'co2MetricTitle', descriptionTranslationKey : 'co2MetricDescription', symbol: 'kg' },
  cyclingFrequency: { isPercentage: true, titleTranslationKey: 'cyclingFrequencyMetricTitle', descriptionTranslationKey : 'cyclingFrequencyMetricDescription', symbol: '%' },
  avgKm: { isPercentage: false, titleTranslationKey: 'avgKmMetricTitle', descriptionTranslationKey : 'avgKmMetricDescription', symbol: 'km' },
  travelTime: { isPercentage: false, titleTranslationKey: 'travelTimeMetricTitle', descriptionTranslationKey : 'travelTimeMetricDescription', symbol: '' },
  usersCount: { isPercentage: false, titleTranslationKey: 'usersCountMetricTitle', descriptionTranslationKey : 'usersCountMetricDescription', symbol: '' }
};

const lineChartMetrics = ["totalKm", "co2","travelTime","avgKm"];

export const metricColorRanges = {
  co2: [
    '#c5e1a5', // verde chiaro ma non slavato
    '#a5d6a7',
    '#81c784',
    '#4caf50',
    '#2e7d32',
  ],
  totalKm: [
    '#fff9c4', // giallo chiaro, puro
    '#fff59d',
    '#fdd835',
    '#fbc02d',
    '#f9a825',
  ],
  cyclingFrequency: [
    '#81d4fa', // azzurro visibile e vivo
    '#4fc3f7',
    '#29b6f6',
    '#039be5',
    '#0277bd',
  ],
  avgKm: [
    '#bcaaa4', // marrone tenue
    '#a1887f',
    '#8d6e63',
    '#6d4c41',
    '#4e342e',
  ],
  travelTime: [
    '#c5e1a5', // simile a co2 ma più spostato verso lime
    '#aed581',
    '#9ccc65',
    '#7cb342',
    '#558b2f',
  ],
  usersCount: [
    '#b2dfdb', // verde acqua chiaro e morbido
    '#80cbc4',
    '#4db6ac',
    '#26a69a',
    '#00796b',
  ],
};


export function OpenDataImpact({interval, startDateUrbanPoints}) {
    const theme = useTheme();
    const { id } = useParams();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const [chartsData, setChartsData] = useState([]);
    const { data: statisticsUpUntilMonth, status } = useGetImpactLineCharts(id, {endDate: dayjs.utc(interval.end).add(1, 'day').valueOf()}, String(id) === 'all', REFETCH_OPTIONS);
    

    useEffect(() => {
        if (status === 'success') {
            const chartsData = Object.keys(metricConfig).map((metricKey) => {

                if (!lineChartMetrics.includes(metricKey))
                    return null;

                const data = [];
                for (const monthData of statisticsUpUntilMonth) {
                    data.push(
                        { month: monthData.month, metricData: monthData[metricKey] }
                    );
                }

                if (data.length === 0) return null;

                return {
                    data: data,
                    metricKey: metricKey,
                };
            }).filter(item => item !== null);

            setChartsData(chartsData);
        }

    }, [statisticsUpUntilMonth, status])

    return (
        <Grid container display={'flex'} justifySelf={'center'} xs={12} width={isDesktop ? '75vw' : '100%'}>
            <Grid container id={'mapChartContainer'} justifyContent={'center'} xs={12} marginY={4}>
                <ImpactGoogleMap interval={interval} id={id}></ImpactGoogleMap>
            </Grid>
            {status === 'success' &&
                chartsData.map((e) => (
                    <Grid item key={`${e.metricKey}-impact-line-chart`} xs={12} md={6} sx={{ marginY: { xs: 1, sm: 2 }, p: { xs: 1, sm: 0.5 } }}>
                        <CustomHoverCard>
                            <CustomLineChart
                                chartMetric={e.metricKey}
                                chartTitle={metricConfig[e.metricKey].titleTranslationKey}
                                seriesData={e.data}
                                metricColor={metricColorRanges[e.metricKey].at(-1)}
                            />
                        </CustomHoverCard>
                    </Grid>
                ))}
            {status === 'loading' &&
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: impactGoogleMapContainerStyle.height,
                        width: impactGoogleMapContainerStyle.width,
                    }}
                >
                    <CircularProgress />
                </Box>}
        </Grid>
    );
}

/**
 * ********************************
 * ********************************
 * ************ CHARTS ************
 * ********************************
 * ********************************
 */

function CustomLineChart  ({chartMetric, chartTitle, seriesData, metricColor})  {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const chartHeight = isDesktop ? '40vh' : '35vh'
    const [chartData, setChartData] = useState({
        seriesData: seriesData ?? []
    });

    // Aggiorna i dati quando intervalData cambia
    useEffect(() => {
        setChartData({
            seriesData
        });

    }, [seriesData]);

    /********* OPZIONI CHART *********/

    const option = {
        title: {text: t(chartTitle), show: true, left: 'center', top: 15},
        colorBy: 'data',
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            backgroundColor: 'rgba(0,0,0,0.7)',
            textStyle: { color: '#fff' },
            transitionDuration: 0.2,
            confine: true,
            formatter: function (params) {
                const p = params[0];
                return `
                    <div>
                        <strong>
                            ${new Date(p.axisValue).toLocaleDateString(i18n.language)}
                        </strong>
                        <br/>
                        <span style="color:${p.color}">●</span>${p.seriesName}&nbsp;&nbsp;
                        <strong>
                            ${(chartMetric === 'travelTime' && formatCompactDurationUpToYears(p.value[1], i18n.language, false))
                            || `${p.value[1].toLocaleString(i18n.language)} ${metricConfig[chartMetric].symbol}`}
                        </strong>
                    </div>
                `;
            }
        },
        grid: { left: '3%', right: '3%', bottom: '3%', containLabel: true, show: true },
        xAxis: [
            {
                type: 'time',
                axisTick: {alignWithLabel: true},
                nameTextStyle: {fontWeight: 'bold', fontSize: isDesktop ? 15 : 12, color: '#333'},
                axisLabel: {
                    fontSize: isDesktop ? 15 : 12,
                    color: '#666',
                    fontFamily: 'Arial',
                    rotate: 45,
                    margin: 16,
                    formatter: {
                        year: '{yyyy}',
                        month: '{MM}/{yyyy}',
                    }
                }
            }
        ],
        yAxis: [
            {
                type: 'value',
                splitNumber: isDesktop ? 4 : 2,
                axisLabel: {
                    fontSize: isDesktop ? 15 : 12, color: '#666', fontFamily: 'Arial',
                    formatter: function (value) {
                        if(chartMetric === 'travelTime')
                            return formatCompactDurationUpToYears(value, i18n.language, true);

                        const {lang, notation} = isDesktop 
                            ? {lang: i18n.language, notation: 'standard'}
                            :  {lang: 'en', notation: 'compact'} // su dispositivi piccolo forzo en per avere 150K / 150M come format

                        const formatted = Intl.NumberFormat(lang, {
                            notation: notation,
                        }).format(value);

                        return `${formatted} ${metricConfig[chartMetric].symbol}`;
                    }
                },
                nameTextStyle: { fontWeight: 'bold', fontSize: isDesktop ? 15 : 12, color: '#333', },
            }
        ],
        series: [
            {
                name: t(chartTitle),
                type: 'line',
                data: chartData.seriesData.map(e => [dayjs(e.month).valueOf(), e.metricData]),
                lineStyle: {
                    color: metricColor,
                    width: 2,
                },
                itemStyle: {
                    color: metricColor,
                },
                emphasis: {
                    itemStyle: {color: 'rgba(0, 0, 0, 0.1)'}
                },
            }
        ],
        dataZoom: [
            {
                type: 'inside',
                orient: 'horizontal',
                start: 0,
                end: 100,
                filterMode: 'weakFilter',
            }
        ]
    };

    return (
        <ReactECharts
            option={option}
            style={{height: chartHeight, width: '100%'}}
        />
    );
};

export function CustomHoverCard({children, sx, ...props}) {
    return (
        <Box
            sx={{
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: '0px 4px 10px rgba(0,0,0,0.3)',
                transition: 'box-shadow 0.3s ease',
                ':hover': {
                    boxShadow: '0px 6px 15px rgba(0,0,0,0.5)',
                },
                ...sx,
            }}
            {...props}
        >
            {children}
        </Box>
    );
}


